import { AfterViewInit, Component, DestroyRef, ElementRef, input, Input, InputSignal, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { IChatMessages, GroupedChatMessages, IChatMessage, IGroupedChatMessage, IChatUser, IChatFileAttachment, AllowedExtensions } from '../../models/chat';
import { Utils } from '../../services/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { UserLogoGenerateComponent } from '../user-logo-generate/user-logo-generate.component';
import { SHARED_MODULES } from '../../../shared.';
import { SessionService } from '../../services/session.service';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { ChatService, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../../services/chat.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';
import { PhTooltipDirective } from '../../directives/ph-tooltip.directive';
import { filter, fromEvent } from 'rxjs';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { LoaderComponent } from '../loader/loader.component';
import { SocketService } from '../../services/socket.service';
import { HumanNumberPipe } from '../../pipes/human-number.pipe';
import { MatDialog } from '@angular/material/dialog';
import { IImagesViewModalData, ImagesViewModalComponent } from '../../modals/images-view-modal/images-view-modal.component';
import { PhUserTooltipComponent } from '../ph-user-tooltip/ph-user-tooltip.component';
import { GetLinkPipe } from '../../pipes/get-link.pipe';
import { PhButtonComponent } from '../ph-button/ph-button.component';

const SCROLL_NEXT_DISTANCE_PX: number = 400;
const AUTO_SCROLLING_TIMEOUT_MS: number = 300;

@Component({
    selector: 'ph-chat-messages',
    templateUrl: './ph-chat-messages.component.html',
    styleUrls: ['./ph-chat-messages.component.scss'],
    imports: [...SHARED_MODULES, UserLogoGenerateComponent, PhIconWrapperComponent, MentionParserPipe, PhTooltipDirective, PhIconComponent, LoaderComponent, HumanNumberPipe, PhUserTooltipComponent, GetLinkPipe, PhButtonComponent]
})
export class PhChatMessagesComponent implements OnInit, AfterViewInit {

    emptySubtitle: InputSignal<string> = input<string>(this.translate.instant('CHAT.WRITE_FOR_USE'));

    private _messages: IChatMessages;
    private _isAutoScrolling: boolean = false;
    private _preloadingMedia: Map<string, HTMLImageElement | HTMLVideoElement> = new Map();

    readonly userId: number;
    groupedMessages: WritableSignal<GroupedChatMessages> = signal<GroupedChatMessages>([]);
    scrollBottomShown: WritableSignal<boolean> = signal<boolean>(false);
    unreadCount: WritableSignal<number> = signal<number>(0);

    @Input('messages') set messages(value: IChatMessages) {
        this.setMessages(value);
        this.initMentionTooltipListeners();
    }

    @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('tooltipAnchor', { read: PhTooltipDirective }) tooltipAnchor!: PhTooltipDirective;

    anchorMentionX: number = 0;
    anchorMentionY: number = 0;
    mentionUserData: WritableSignal<IChatUser> = signal<IChatUser>(null);
    loadedAttachmentKeys: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
    failedAttachmentKeys: WritableSignal<Set<string>> = signal<Set<string>>(new Set());

    constructor(
        private dialog: MatDialog,
        private destroyRef: DestroyRef,
        private translate: TranslateService,
        private sessionService: SessionService,
        private chatService: ChatService,
        private socketService: SocketService
    ) {
        this.userId = this.sessionService.defaults.user_id ?? -1;

        this.destroyRef.onDestroy(() => {
            this._preloadingMedia.forEach((element: HTMLImageElement | HTMLVideoElement) => {
                element.onload = null;
                element.onerror = null;
                if (element instanceof HTMLVideoElement) {
                    element.onloadeddata = null;
                    element.pause();
                    element.src = '';
                    element.load();
                } else {
                    element.src = '';
                }
            });
            this._preloadingMedia.clear();
        });
    }

    ngOnInit(): void {
        this.chatService.scrollToMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((messageId: number) => {
                const element = document.getElementById('message-' + messageId);
                if (!element) {
                    this.chatService.loadAroundMessage$.next(messageId);
                } else {
                    this.scrollToMessage(messageId);
                }
            });
    }

    ngAfterViewInit(): void {
		if (!this._messages?.previous) this.scrollToBottom();
        this.chatService.inputMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.checkScrollBottom());

        this.chatService.scrollToBottom$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => setTimeout(() => this.onScrollBottom('smooth')));

        this.chatService.newMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this._messages?.previous) return;
                setTimeout(() => this.onScrollBottom('smooth'));
            });

        this.chatService.checkScrollBottomPosition$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.checkScrollBottom());

        this.socketService.chatUnreadCount
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: { unread: number }): void => {
                this.unreadCount.set(data.unread);
            });

        this.initMentionTooltipListeners();
	}

    private initMentionTooltipListeners(): void {
        if (!this.messagesContainer) return;
        
        fromEvent(this.messagesContainer.nativeElement, 'mouseover')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event: MouseEvent) => {
                    const target: HTMLElement = event.target as HTMLElement;
                    return !!target.closest('.mention-tag');
                })
            )
            .subscribe((event: MouseEvent) => {
                const target: HTMLElement = event.target as HTMLElement;
                const mentionElement: HTMLSpanElement = target.closest('.mention-tag');
                const messageContainer: HTMLElement = mentionElement.closest('.message-container') as HTMLElement;
                const messageId: number = Number(messageContainer?.id.replace('message-', ''));
                const message: IChatMessage = this._messages.results.find((message: IChatMessage) => message.id === messageId);
                const user = message?.mentioned_users?.find(user => user.id === Number(mentionElement.id));
                this.mentionUserData.set(user);
                const rect = mentionElement.getBoundingClientRect();

                this.anchorMentionX = rect.left + rect.width / 2;
                this.anchorMentionY = rect.top;

                if (this.tooltipAnchor) this.tooltipAnchor.show();
            });

        fromEvent(this.messagesContainer.nativeElement, 'mouseout')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event: MouseEvent) => {
                    const target = event.target as HTMLElement;
                    return !!target.closest('.mention-tag');
                })
            )
            .subscribe(() => {
                if (this.tooltipAnchor) this.tooltipAnchor.hide();
            });
    }

    private groupMessages(messages: Array<IChatMessage>): GroupedChatMessages {
		const today: Date = new Date();
		const yesterday: Date = new Date();
		yesterday.setDate(today.getDate() - 1);

        const todayTranslate: string = this.translate.instant('TODAY');
        const yesterdayTranslate: string = this.translate.instant('YESTERDAY');

		const groupedByDate: { [key: string]: Array<IChatMessage> } = messages
            .sort((a, b) => {
                const aTime = Utils.parseIsoWithMicro(a.created_at);
                const bTime = Utils.parseIsoWithMicro(b.created_at);
                return aTime - bTime;
            })
            .reduce((acc: { [key: string]: Array<IChatMessage> } , message: IChatMessage) => {
                const date: string = new Date(message.created_at).toISOString().split('T')[0];
                const messageDate: Date = new Date(date);

                let dateLabel: string;
                if (Utils.isSameDay(messageDate, today)) {
                    dateLabel = todayTranslate;
                } else if (Utils.isSameDay(messageDate, yesterday)) {
                    dateLabel = yesterdayTranslate;
                } else {
                    dateLabel = messageDate.toLocaleDateString('uk');
                }

                if (!acc[dateLabel]) acc[dateLabel] = [];
                acc[dateLabel].push(message);
                return acc;
            }, {} as { [key: string]: Array<IChatMessage> });

        const groupedMessages: GroupedChatMessages = Object.keys(groupedByDate).map((dateLabel: string) => {
            const dateMessages: Array<IChatMessage> = groupedByDate[dateLabel];
            const groups: { user: IChatUser, messages: Array<IChatMessage> }[] = [];

            dateMessages.forEach((message: IChatMessage) => {
                if (message.status !== 'failed') {
                    if (typeof message.id === 'string') {
                        message.status = 'pending';
                    } else if (message.read_by?.length) {
                        message.status = 'read';
                    } else {
                        message.status = 'sent';
                    }
                }

                message.attachments?.forEach((attachment: IChatFileAttachment) => {
                    attachment.icon = this.chatService.getIconForFileExtension(attachment.name.split('.').pop().toLowerCase());
                    const isImage = IMAGE_EXTENSIONS.includes(attachment.content_type as AllowedExtensions);
                    const isVideo = VIDEO_EXTENSIONS.includes(attachment.content_type as AllowedExtensions);

                    if (isImage || isVideo) {
                        attachment.previewUrl = attachment.file;
                        attachment.type = isImage ? 'image' : 'video';
                        if (!this.loadedAttachmentKeys().has(attachment.key)) {
                            this.preloadAttachment(attachment);
                        }
                    } else {
                        attachment.type = 'file';
                    }
                });

                const lastGroup = groups[groups.length - 1];
                const messageTime = new Date(message.created_at).getTime();

                if (lastGroup && lastGroup.user.id === message.user.id) {
                    const lastMessageTime = new Date(lastGroup.messages[lastGroup.messages.length - 1].created_at).getTime();
                    if (messageTime - lastMessageTime <= 600000) {
                        lastGroup.messages.push(message);
                        return;
                    }
                }

                groups.push({
                    user: message.user,
                    messages: [message]
                });
            });

            return {
                dateLabel,
                groups
            };
        });

        return groupedMessages;
	}

    private setMessages(messagesData: IChatMessages): void {
        const container: HTMLDivElement = this.messagesContainer?.nativeElement;
        const oldScrollHeight: number = container?.scrollHeight || 0;
        const oldScrollTop: number = container?.scrollTop || 0;

        this._messages = messagesData;
        const grouped: GroupedChatMessages = this.groupMessages(messagesData.results);
        this.groupedMessages.set(grouped);
        this._isAutoScrolling = true;

        if (!Utils.isNullOrUndefined(messagesData.anchor_message_id)) {
            setTimeout(() => {
                this.chatService.scrollToMessage$.next(messagesData.anchor_message_id);
                messagesData.anchor_message_id = undefined;
            });
        } else if (messagesData.anchor_date) {
            const dayIndex: number = this.groupedMessages().findIndex((group: IGroupedChatMessage) => 
                group.groups[0]?.messages[0]?.created_at.includes(messagesData.anchor_date)
            );
            messagesData.anchor_date = undefined;
            if (dayIndex !== -1) setTimeout(() => this.scrollToDay(dayIndex));
        } else if (this.chatService.isLoadingNext() && container && oldScrollHeight > 0) {
            setTimeout(() => {
                const newScrollHeight = container.scrollHeight;
                container.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
            });
        }

        this.chatService.isLoadingPrevious.set(false);
        this.chatService.isLoadingNext.set(false);
        setTimeout(() => this._isAutoScrolling = false, AUTO_SCROLLING_TIMEOUT_MS);
    }

    private scrollToBottom(behavior: ScrollBehavior = 'auto'): void {
        this._isAutoScrolling = true;
        const container: HTMLDivElement = this.messagesContainer?.nativeElement;
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: behavior });
        }
        setTimeout(() => this._isAutoScrolling = false, AUTO_SCROLLING_TIMEOUT_MS);
	}

    private scrollToDay(index: number): void {
        this._isAutoScrolling = true;
        const element: HTMLElement = document.getElementById('day-' + index);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => this._isAutoScrolling = false, AUTO_SCROLLING_TIMEOUT_MS);
    }

    public scrollToMessage(messageId: number): void {
        this._isAutoScrolling = true;
        const element = document.getElementById('message-' + messageId);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => this._isAutoScrolling = false, AUTO_SCROLLING_TIMEOUT_MS);
    }

    openHelpdesk(): void {
        const links = {
            en: 'https://helpdesk.planohero.com/en/article/chat-web-version-8o1snl/',
            uk: 'https://helpdesk.planohero.com/uk/article/chat-veb-versiya-sxrtg8/',
            ru: 'https://helpdesk.planohero.com/ru/article/chat-veb-versiya-tze0rn/'
        };

        window.open(links[this.translate.getCurrentLang()] ?? links.en, '_blank');
    }

    onMessagesScroll(): void {
		const messageContainer: HTMLDivElement = this.messagesContainer?.nativeElement;
        if (!messageContainer) return;

        const value: boolean = messageContainer.scrollHeight - messageContainer.scrollTop > messageContainer.clientHeight + SCROLL_NEXT_DISTANCE_PX;
        this.scrollBottomShown.set(!!this._messages?.previous || value);

        if (this._isAutoScrolling) return;

        if (this._messages?.next && !this.chatService.isLoadingNext() && messageContainer.scrollTop <= SCROLL_NEXT_DISTANCE_PX) {
            this.chatService.loadNextMessages$.next();
        }

        const distFromBottom = messageContainer.scrollHeight - messageContainer.scrollTop - messageContainer.clientHeight;
        if (this._messages?.previous && !this.chatService.isLoadingPrevious() && distFromBottom <= SCROLL_NEXT_DISTANCE_PX) {
             this.chatService.loadPreviousMessages$.next();
        }
	}

    public checkScrollBottom(): void {
        if (this._isAutoScrolling) return;
        const messageContainer: HTMLDivElement = this.messagesContainer?.nativeElement;
        if (!messageContainer) return;
		const needScrollMessages: boolean = Math.abs(messageContainer.scrollHeight - messageContainer.scrollTop - messageContainer.clientHeight) < 10;
        if (needScrollMessages) setTimeout(() => this.onScrollBottom());
    }

    public onDaySelect(index: number, date?: string): void {
        if (!index && this._messages.next) {
            this.chatService.scrollToDay$.next(date?.split('T')[0]);
        } else {
            this.scrollToDay(index);
        }
    }

    onMessageSelect(messageId: number): void {
        this.chatService.scrollToMessage$.next(messageId);
    }

    public onReplyMessage(message: IChatMessage): void {
        this.chatService.replyMessage$.next(message);
    }

    public onScrollBottom(behavior: ScrollBehavior = 'auto'): void {
        if (this._messages?.previous) {
            this.chatService.loadLastMessages$.next();
        } else {
            this.scrollToBottom(behavior);
        }
    }

    public markAttachmentLoaded(key: string): void {
        this.loadedAttachmentKeys.update((set) => new Set(set).add(key));
    }

    public markAttachmentFailed(key: string): void {
        this.loadedAttachmentKeys.update((set) => new Set(set).add(key));
        this.failedAttachmentKeys.update((set) => new Set(set).add(key));
    }

    public openImage(file: IChatFileAttachment, attachments: Array<IChatFileAttachment>): void {
        const files: Array<IChatFileAttachment> = attachments.filter((file: IChatFileAttachment) => file.previewUrl);

        this.dialog
            .open<ImagesViewModalComponent, IImagesViewModalData>(ImagesViewModalComponent, {
                width: 'auto',
                autoFocus: false,
                backdropClass: 'back-drop',
                data: {
                    images: files.map((file: IChatFileAttachment) => file.previewUrl),
                    types: files.map((file: IChatFileAttachment) => VIDEO_EXTENSIONS.includes(file.content_type as AllowedExtensions) ? 'video' : 'image'),
                    names: files.map((file: IChatFileAttachment) => file.name),
                    index: files.indexOf(file),
                    showMainButton: false,
                    showSecondaryButton: false
                }
            })
            .afterClosed().subscribe();
    }

    downloadFile(file: IChatFileAttachment): void {
        fetch(file.file)
            .then(response => response.blob())
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(error => console.error('Download failed', error));
    }

    private preloadAttachment(attachment: IChatFileAttachment): void {
        const key: string = attachment.key;
        if (attachment.type === 'image') {
            const img: HTMLImageElement = new Image();
            img.onload = () => {
                this._preloadingMedia.delete(key);
                this.markAttachmentLoaded(key);
            };
            img.onerror = () => {
                this._preloadingMedia.delete(key);
                this.markAttachmentFailed(key);
            };
            this._preloadingMedia.set(key, img);
            img.src = attachment.previewUrl;
        } else if (attachment.type === 'video') {
            const video: HTMLVideoElement = document.createElement('video');
            video.onloadeddata = () => {
                this._preloadingMedia.delete(key);
                this.markAttachmentLoaded(key);
            };
            video.onerror = () => {
                this._preloadingMedia.delete(key);
                this.markAttachmentFailed(key);
            };
            this._preloadingMedia.set(key, video);
            video.src = attachment.previewUrl;
            video.load();
        }
    }
}